import React from "react";
import { Link } from "react-router-dom";
import { formatCurrency, ELECTION_TYPES, GOVERNING_REGULATIONS } from "../../lib/costEngine";
import { fmtDate, fmtDateTime } from "../../lib/proposalPdf";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Download,
  Send,
  Mail,
  Users,
  Building2,
  MapPin,
  CalendarDays,
  Loader2,
  Eye,
  ExternalLink,
  CheckCircle2,
  FileText,
  Link2,
  RefreshCw,
} from "lucide-react";

export function ProposalDetailPanel({
  selected,
  busy,
  onView,
  onDownload,
  onResend,
  onRegenerate,
  onCopyShareLink,
  onDownloadDoc,
  onOpenEmail,
}) {
  if (!selected) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-muted-foreground">
          Select a proposal to view details.
        </CardContent>
      </Card>
    );
  }

  const mailing = [selected.lead?.street, selected.lead?.city, selected.lead?.province, selected.lead?.postalCode]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="space-y-5" data-testid="proposal-detail">
      {/* CRM card */}
      <Card>
        <CardContent className="p-6">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-3">
            Client / CRM
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Name:</span>
              <span className="font-medium" data-testid="detail-client-name">{selected.lead?.name || "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Email:</span>
              <span className="font-medium">{selected.lead?.email || "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Title:</span>
              <span className="font-medium">{selected.lead?.jobTitle || "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <Building2 className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">First Nation:</span>
              <span className="font-medium">{selected.lead?.firstNation || selected.lead?.organization || "—"}</span>
              {selected.lead?.firstNationId && (
                <Badge
                  data-testid="fn-verified-badge"
                  variant="outline"
                  className="gap-1 border-green-300 bg-green-50 text-green-700"
                  title={`Linked to CRM roster (${selected.lead.firstNationId})`}
                >
                  <CheckCircle2 className="h-3 w-3" /> Verified official
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Phone:</span>
              <span className="font-medium">{selected.lead?.phone || "—"}</span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-primary" />
              <span className="text-muted-foreground">Captured:</span>
              <span className="font-medium">{fmtDate(selected.createdAt)}</span>
            </div>
            {selected.preparedBy && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-primary" />
                <span className="text-muted-foreground">Prepared by:</span>
                <span className="font-medium" data-testid="detail-prepared-by">{selected.preparedBy}</span>
              </div>
            )}
            <div className="flex items-start gap-2 sm:col-span-2">
              <MapPin className="h-4 w-4 text-primary mt-0.5" />
              <span className="text-muted-foreground">Mailing:</span>
              <span className="font-medium">{mailing || "—"}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Proposal copy */}
      <Card data-testid="proposal-copy">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="font-display font-700 text-xl text-foreground">{selected.eventTitle}</h2>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
                <span className="inline-flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5" /> {selected.location || "—"}
                </span>
                <span>·</span>
                <span>{ELECTION_TYPES[selected.electionType]?.label || selected.electionType}</span>
                <span>·</span>
                <span>{Number(selected.membershipSize).toLocaleString()} members</span>
              </div>
              {selected.governingRegulation && (
                <div className="mt-2">
                  <Badge variant="secondary" className="text-xs" data-testid="detail-governing-reg">
                    {GOVERNING_REGULATIONS[selected.governingRegulation] || selected.governingRegulation}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Line Item</TableHead>
                <TableHead className="text-right">Amount</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(selected.items || []).map((it) => (
                <TableRow key={it.name}>
                  <TableCell className="font-medium">{it.name}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatCurrency(it.amount)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell className="text-right text-muted-foreground">Subtotal</TableCell>
                <TableCell className="text-right tabular-nums font-semibold">{formatCurrency(selected.subtotal)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="text-right text-muted-foreground">GST / Tax</TableCell>
                <TableCell className="text-right tabular-nums">{formatCurrency(selected.tax)}</TableCell>
              </TableRow>
              <TableRow className="bg-accent">
                <TableCell className="text-right font-display font-700">Total</TableCell>
                <TableCell className="text-right tabular-nums font-display font-700 text-primary" data-testid="detail-total">
                  {formatCurrency(selected.total)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Actions */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm">
            {selected.sentAt ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-muted-foreground">Sent to recipient on</span>
                <span className="font-medium" data-testid="detail-sent-at">{fmtDateTime(selected.sentAt)}</span>
              </>
            ) : (
              <span className="text-muted-foreground" data-testid="detail-sent-at">Not yet sent to recipient.</span>
            )}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="view-proposal-btn" onClick={onView} disabled={busy !== ""} className="gap-2">
              {busy === "view" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              View Proposal
            </Button>
            <Button data-testid="download-proposal-btn" variant="outline" onClick={onDownload} disabled={busy !== ""} className="gap-2">
              {busy === "download" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Download PDF
            </Button>
            <Button data-testid="resend-proposal-btn" variant="outline" onClick={onResend} disabled={busy !== ""} className="gap-2">
              {busy === "resend" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {selected.sentAt ? "Resend to Client" : "Send to Client"}
            </Button>
            <Link
              to={`/admin/client/${encodeURIComponent(selected.lead?.email || "")}`}
              data-testid="open-client-profile-link"
            >
              <Button variant="ghost" className="gap-2">
                <ExternalLink className="h-4 w-4" /> Open Client Profile
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button data-testid="send-email-btn" variant="secondary" onClick={onOpenEmail} disabled={busy !== ""} className="gap-2">
              <Mail className="h-4 w-4" /> Send Email
            </Button>
            <Button data-testid="copy-share-link-btn" variant="ghost" onClick={onCopyShareLink} disabled={busy !== ""} className="gap-2">
              {busy === "share" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Copy Shareable Link
            </Button>
            <Button data-testid="regenerate-pdf-btn" variant="ghost" onClick={onRegenerate} disabled={busy !== ""} className="gap-2">
              {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Regenerate PDF
            </Button>
            {selected.documentKey && (
              <Button data-testid="download-doc-btn" variant="ghost" onClick={onDownloadDoc} disabled={busy !== ""} className="gap-2">
                {busy === "doc" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
                Governing Document
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
